"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  isLsatScore,
  LSAT_ROWS,
  lsatGap,
  lsatLowestForPercentile,
  lsatRow,
} from "@/lib/tool-kit/exam-scores/lsat";

/** English strings. A translated content file overrides these through `ui`. */
export const LSAT_PERCENTILE_CALCULATOR_STRINGS = {
  inputHeading: "Your LSAT score",
  scoreLabel: "LSAT score (120–180)",
  compareLabel: "Compare with another score (optional)",
  invalid: "An LSAT score is a whole number from 120 to 180.",
  compareInvalid:
    "The comparison score must be a whole number from 120 to 180.",
  results: "Percentile rank",
  percentile: "Percentile for {score}",
  exact: "Percent scoring lower, to two decimals",
  whole: "LSAC's whole-number percentile",
  atOrAbove: "Scored {score} or higher",
  lowerLine:
    "{percent}% of scores in the table were lower than {score}, and {above}% were {score} or higher.",
  bottom: "A score of 120 is the lowest possible, so no scores fall below it.",
  compareHeading: "Comparison",
  compareLine:
    "{from} is at {fromPercent}% and {to} is at {toPercent}% — a gap of {gap} percentage points.",
  targetHeading: "Work backward from a percentile",
  targetLabel: "Percent of test takers you want to outscore (1–99)",
  targetResult:
    "The lowest score that outscores at least {target}% is {score} ({percent}% scored lower).",
  targetNone: "No LSAT score outscores that share in this table.",
  targetInvalid: "Enter a number from 1 to 99.",
  enterScore: "Enter an LSAT score to see its percentile rank.",
  tableNote:
    "LSAC's percentile table for the {years} testing years. A percentile rank is the percentage of test scores that are lower than the given scaled score.",
  years: "2023-2024, 2024-2025 and 2025-2026",
  tableHeading: "LSAC percentile table",
  colScore: "LSAT score",
  colHundredths: "Percent below (hundredths)",
  colTenths: "Percent below (tenths)",
  colWhole: "Percent below (whole numbers)",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "LSAT {score}: {percent}% of scores were lower (LSAC, {years})",
} as const;

type Strings = typeof LSAT_PERCENTILE_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

const TWO = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
const ONE = { minimumFractionDigits: 1, maximumFractionDigits: 1 };

function read(raw: string): number | null {
  return raw.trim() === "" ? null : parseNumberInput(raw);
}

function Comparison({
  from,
  to,
  strings,
  fmt,
}: {
  from: number;
  to: number;
  strings: Strings;
  fmt: Format;
}) {
  const a = lsatRow(from);
  const b = lsatRow(to);
  const gap = lsatGap(from, to);
  if (!(a && b) || gap === null) {
    return null;
  }
  return (
    <div className="space-y-1">
      <h3 className={WIDGET_H3}>{strings.compareHeading}</h3>
      <p className="text-foreground text-sm">
        {t(strings.compareLine, {
          from: fmt(from),
          fromPercent: fmt(a.hundredths, TWO),
          to: fmt(to),
          toPercent: fmt(b.hundredths, TWO),
          gap: fmt(Math.abs(gap), TWO),
        })}
      </p>
    </div>
  );
}

function TargetPanel({ strings, fmt }: { strings: Strings; fmt: Format }) {
  const [raw, setRaw] = useState("90");
  const value = read(raw);
  const valid = value !== null && value >= 1 && value <= 99;
  const score = valid ? lsatLowestForPercentile(value) : null;
  const row = score === null ? null : lsatRow(score);
  let message: string | null = null;
  if (valid && row) {
    message = t(strings.targetResult, {
      target: fmt(value, { maximumFractionDigits: 2 }),
      score: fmt(row.score),
      percent: fmt(row.hundredths, TWO),
    });
  } else if (valid) {
    message = strings.targetNone;
  }
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.targetHeading}</h3>
      <TextField
        id="lsat-target"
        label={strings.targetLabel}
        numeric
        onChange={setRaw}
        value={raw}
      />
      <div aria-live="polite" className="text-foreground text-sm">
        {message}
        {valid || raw.trim() === "" ? null : (
          <Notice>{strings.targetInvalid}</Notice>
        )}
      </div>
    </section>
  );
}

export default function LsatPercentileCalculatorWidget() {
  const strings = useToolUi(LSAT_PERCENTILE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("165");
  const [compareRaw, setCompareRaw] = useState("");
  const score = read(raw);
  const compare = read(compareRaw);
  const row = isLsatScore(score) ? lsatRow(score) : null;
  const compareValid = isLsatScore(compare);

  return (
    <div className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.inputHeading}</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="lsat-score"
          label={strings.scoreLabel}
          numeric
          onChange={setRaw}
          value={raw}
        />
        <TextField
          id="lsat-compare"
          label={strings.compareLabel}
          numeric
          onChange={setCompareRaw}
          value={compareRaw}
        />
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {raw.trim() === "" ? <Notice>{strings.enterScore}</Notice> : null}
        {raw.trim() !== "" && !row ? <Notice>{strings.invalid}</Notice> : null}
        {compareRaw.trim() !== "" && !compareValid ? (
          <Notice>{strings.compareInvalid}</Notice>
        ) : null}
        {row ? (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={t(strings.percentile, { score: fmt(row.score) })}
                value={`${fmt(row.hundredths, TWO)}%`}
              />
              <StatCard label={strings.whole} value={`${fmt(row.whole)}%`} />
              <StatCard
                label={t(strings.atOrAbove, { score: fmt(row.score) })}
                value={`${fmt(Math.round((100 - row.hundredths) * 100) / 100, TWO)}%`}
              />
            </StatGrid>
            <p className="text-muted-foreground text-sm">
              {t(strings.lowerLine, {
                percent: fmt(row.hundredths, TWO),
                above: fmt(Math.round((100 - row.hundredths) * 100) / 100, TWO),
                score: fmt(row.score),
              })}
            </p>
            {row.score === 120 ? (
              <p className="text-muted-foreground text-sm">{strings.bottom}</p>
            ) : null}
            {isLsatScore(compare) ? (
              <Comparison
                fmt={fmt}
                from={row.score}
                strings={strings}
                to={compare}
              />
            ) : null}
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyResult}
              small
              text={t(strings.summary, {
                score: fmt(row.score),
                percent: fmt(row.hundredths, TWO),
                years: strings.years,
              })}
            />
          </>
        ) : null}
        <p className="text-muted-foreground text-sm">
          {t(strings.tableNote, { years: strings.years })}
        </p>
      </section>

      <TargetPanel fmt={fmt} strings={strings} />

      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        <DataTable
          head={[
            strings.colScore,
            strings.colHundredths,
            strings.colTenths,
            strings.colWhole,
          ]}
          rows={LSAT_ROWS.map((entry) => [
            fmt(entry.score),
            `${fmt(entry.hundredths, TWO)}%`,
            `${fmt(entry.tenths, ONE)}%`,
            `${fmt(entry.whole)}%`,
          ])}
        />
      </section>
    </div>
  );
}
